// SportContext — frontend mirror of the backend sport-pack registry.
//
// Sport packs come from `GET /api/v1/public/sports/:id`. They're effectively
// static per release, so we cache them in `localStorage` and the in-memory
// store keyed by sport id. The hook resolves to:
//
//   useSport()              → the active sport pack (uses active-season /
//                              tenant default), with a stub fallback while
//                              loading so callers don't crash.
//   useSport(sportId)       → the pack for a specific sport id.
//   useSportForSeason(id)   → the pack for a season's sport_id (looked up
//                              from TenantInfo.seasons[]).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLeague } from './LeagueContext'
import { setPublicSportContext } from '../services/api'
import { readStoredSport, resolveStoredSport, writeStoredSport, type StoredSportSnapshot } from './storedSport'
import { localizePack, localizePacks, sportWords } from './sportPackI18n'
import { useLocale } from '@/i18n'

// ────────────────────────────────────────────────────────────────────────
// Wire types — mirror backend/src/sports/types.ts SportPackPayload.
// ────────────────────────────────────────────────────────────────────────

export interface StatDef {
  key: string
  label: string
  short: string
  storage: 'column' | 'jsonb'
  configKey?: string
  gameLog: boolean
  perGameAverage: boolean
  agg: 'sum' | 'avg' | 'max' | 'none'
  sortable: boolean
  min?: number
  max?: number
  group?: string
  /** ScoreLM Scorebook: increment buttons offered when tapping this stat (points → [1,2,3]). Defaults to [1]. */
  tapIncrements?: number[]
}

export interface DerivedStatDef {
  key: string
  label: string
  short: string
}

export interface PositionDef {
  code: string
  label: string
  group?: string
}

export interface PeriodConfig {
  kind: 'quarters' | 'halves' | 'sets' | 'innings' | 'periods'
  sequence: string[]
  regularSequence: string[]
  lengthSec: number
  allowOT: boolean
  intermissionPeriods: string[]
  prettyLabels: Record<string, string>
}

export interface LeaderboardCategory {
  key: string
  label: string
  sortKey: string
  shortLabel: string
}

export interface StandingsColumnDef {
  key: string
  label: string
  description?: string
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  primary?: boolean
}

/** Fan-facing nouns that vary by sport (mirror of backend SportTerms). Read
 *  them through `useLabels()` (src/site/core/labels), never directly. */
export interface SportTerms {
  game: string
  games: string
  gamesPlayedShort: string
  perGame: string
  roundRobinRound: string
  onField: string
  roster: string
  start: string
  final: string
  finalShort: string
}

/**
 * A live-entry action that moves several stats in one tap (made/missed shots,
 * #585): one Scorebook event whose stat_key is `key`, expanded by the API.
 */
export interface StatActionDef {
  key: string
  /** Button text: "Made 3". */
  label: string
  /** Shot type: "3PT". */
  short: string
  /** Play-by-play: "Missed 3PT". */
  feedLabel: string
  effects: Record<string, number>
  configKeys: string[]
}

export interface SportPack {
  id: string
  displayName: string
  stats: StatDef[]
  teamStats: StatDef[]
  derivedStats: DerivedStatDef[]
  /** Made/missed shot actions (basketball); a season opts in by tracking their stats. */
  statActions?: StatActionDef[]
  primaryStatKeys: string[]
  scoreSortKey: string
  leaderboardCategories: LeaderboardCategory[]
  teamComparisonKeys: string[]
  periods: PeriodConfig
  defaultClockSeconds: number
  clockDirection: 'down' | 'up'
  allowTie: boolean
  positions: PositionDef[]
  jerseyRange: [number, number]
  standings: { columns: StandingsColumnDef[] }
  scoreBugDefaultShell: string
  venueLabel: { singular: string; plural: string }
  /** Optional: packs cached in localStorage before this field shipped lack
   *  it. `useLabels()` falls back to its per-sport DEFAULT_TERMS. */
  terms?: SportTerms
}

// ────────────────────────────────────────────────────────────────────────
// Static fallback — used until the first /public/sports fetch resolves.
// Keeping a basketball-shaped stub here means existing code that reads
// `useSport().stats` or `useSport().positions` doesn't crash on first paint.
// ────────────────────────────────────────────────────────────────────────

const FALLBACK_BASKETBALL: SportPack = {
  id: 'basketball',
  displayName: 'Basketball',
  // Keep in sync with backend/src/sports/basketball/stats.ts — this is the
  // fallback the SportProvider seeds with before /public/sports/basketball
  // resolves. If a key is missing here the Settings page's Column Order
  // Preview renders that key raw (e.g. "field_goals_made") on first paint.
  stats: [
    { key: 'points', label: 'Points', short: 'PTS', storage: 'column', configKey: 'track_points', gameLog: true, perGameAverage: true, agg: 'sum', sortable: true, min: 0, max: 200, group: 'scoring', tapIncrements: [1, 2, 3] },
    { key: 'rebounds', label: 'Rebounds', short: 'REB', storage: 'column', configKey: 'track_rebounds', gameLog: true, perGameAverage: true, agg: 'sum', sortable: true, min: 0, max: 50, group: 'rebounding' },
    { key: 'assists', label: 'Assists', short: 'AST', storage: 'column', configKey: 'track_assists', gameLog: true, perGameAverage: true, agg: 'sum', sortable: true, min: 0, max: 30, group: 'playmaking' },
    { key: 'steals', label: 'Steals', short: 'STL', storage: 'column', configKey: 'track_steals', gameLog: true, perGameAverage: true, agg: 'sum', sortable: true, min: 0, max: 15, group: 'defense' },
    { key: 'blocks', label: 'Blocks', short: 'BLK', storage: 'column', configKey: 'track_blocks', gameLog: true, perGameAverage: true, agg: 'sum', sortable: true, min: 0, max: 15, group: 'defense' },
    { key: 'turnovers', label: 'Turnovers', short: 'TO', storage: 'column', configKey: 'track_turnovers', gameLog: true, perGameAverage: true, agg: 'sum', sortable: true, min: 0, max: 20, group: 'playmaking' },
    { key: 'fouls', label: 'Fouls', short: 'FLS', storage: 'column', configKey: 'track_fouls', gameLog: true, perGameAverage: false, agg: 'sum', sortable: true, min: 0, max: 10, group: 'fouls' },
    { key: 'field_goals_made', label: 'FG Made', short: 'FGM', storage: 'column', configKey: 'track_field_goals', gameLog: false, perGameAverage: false, agg: 'sum', sortable: true, min: 0, max: 30, group: 'shooting' },
    { key: 'field_goals_attempted', label: 'FG Attempted', short: 'FGA', storage: 'column', configKey: 'track_field_goals', gameLog: false, perGameAverage: false, agg: 'sum', sortable: true, min: 0, max: 40, group: 'shooting' },
    { key: 'three_pointers_made', label: '3P Made', short: '3PM', storage: 'column', configKey: 'track_three_pointers', gameLog: true, perGameAverage: false, agg: 'sum', sortable: true, min: 0, max: 20, group: 'shooting' },
    { key: 'three_pointers_attempted', label: '3P Attempted', short: '3PA', storage: 'column', configKey: 'track_three_point_attempts', gameLog: false, perGameAverage: false, agg: 'sum', sortable: true, min: 0, max: 25, group: 'shooting' },
    { key: 'free_throws_made', label: 'FT Made', short: 'FTM', storage: 'column', configKey: 'track_free_throws', gameLog: false, perGameAverage: false, agg: 'sum', sortable: true, min: 0, max: 25, group: 'shooting' },
    { key: 'free_throws_attempted', label: 'FT Attempted', short: 'FTA', storage: 'column', configKey: 'track_free_throws', gameLog: false, perGameAverage: false, agg: 'sum', sortable: true, min: 0, max: 30, group: 'shooting' },
    { key: 'minutes_played', label: 'Minutes', short: 'MIN', storage: 'column', configKey: 'track_minutes', gameLog: false, perGameAverage: true, agg: 'sum', sortable: true, min: 0, max: 60, group: 'minutes' },
  ],
  teamStats: [],
  derivedStats: [
    { key: 'ppg', label: 'Points / Game', short: 'PPG' },
    { key: 'rpg', label: 'Rebounds / Game', short: 'RPG' },
    { key: 'apg', label: 'Assists / Game', short: 'APG' },
    { key: 'spg', label: 'Steals / Game', short: 'SPG' },
    { key: 'bpg', label: 'Blocks / Game', short: 'BPG' },
    { key: 'fg_pct', label: 'FG %', short: 'FG%' },
    { key: 'tp_pct', label: '3PT %', short: '3P%' },
    { key: 'ft_pct', label: 'FT %', short: 'FT%' },
  ],
  primaryStatKeys: ['ppg', 'rpg', 'apg'],
  scoreSortKey: 'points',
  leaderboardCategories: [
    { key: 'scoring', label: 'Scoring Leader', sortKey: 'ppg', shortLabel: 'PPG' },
    { key: 'rebounds', label: 'Rebounds Leader', sortKey: 'rpg', shortLabel: 'RPG' },
    { key: 'assists', label: 'Assists Leader', sortKey: 'apg', shortLabel: 'APG' },
    { key: 'steals', label: 'Steals Leader', sortKey: 'spg', shortLabel: 'SPG' },
    { key: 'threes', label: '3PT Leader', sortKey: 'three_pointers_made', shortLabel: '3PM' },
  ],
  teamComparisonKeys: ['points', 'rebounds', 'assists', 'steals', 'blocks', 'three_pointers_made'],
  periods: {
    kind: 'quarters',
    sequence: ['Q1', 'Q2', 'HT', 'Q3', 'Q4', 'OT', 'OT2', 'Final'],
    regularSequence: ['Q1', 'Q2', 'HT', 'Q3', 'Q4'],
    lengthSec: 720,
    allowOT: true,
    intermissionPeriods: ['HT', 'Final'],
    prettyLabels: { Q1: '1st Quarter', Q2: '2nd Quarter', HT: 'Halftime', Q3: '3rd Quarter', Q4: '4th Quarter', OT: 'Overtime', OT2: '2nd Overtime', Final: 'Final' },
  },
  defaultClockSeconds: 720,
  clockDirection: 'down',
  allowTie: false,
  positions: [
    { code: 'PG', label: 'Point Guard', group: 'guard' },
    { code: 'SG', label: 'Shooting Guard', group: 'guard' },
    { code: 'SF', label: 'Small Forward', group: 'forward' },
    { code: 'PF', label: 'Power Forward', group: 'forward' },
    { code: 'C', label: 'Center', group: 'center' },
  ],
  jerseyRange: [0, 99],
  standings: {
    columns: [
      { key: 'gp', label: 'GP', align: 'center', sortable: true },
      { key: 'wins', label: 'W', align: 'center', sortable: true, primary: true },
      { key: 'losses', label: 'L', align: 'center', sortable: true },
      { key: 'points_for', label: 'PF', align: 'center', sortable: true },
      { key: 'points_against', label: 'PA', align: 'center', sortable: true },
      { key: 'point_diff', label: '+/-', align: 'center', sortable: true },
    ],
  },
  scoreBugDefaultShell: 'bottom-bar',
  venueLabel: { singular: 'Court', plural: 'Courts' },
}

// ────────────────────────────────────────────────────────────────────────
// Context + provider
// ────────────────────────────────────────────────────────────────────────

interface SportContextType {
  /** Sport packs keyed by id. Populated on demand. */
  packs: Record<string, SportPack>
  /** Active sport id — derived from the active season or tenant default. */
  activeSportId: string
  /**
   * Set the active sport id (e.g. when a page is scoped to a specific season).
   * `{ persist: true }` also stores it as the fan's pick for this host
   * (`mcl_active_sport_<host>`), which the restore below reads back.
   */
  setActiveSport: (id: string, opts?: { persist?: boolean }) => void
  /**
   * Restore the fan's stored sport for this host (P0, site templates §6.0).
   * Called by `useStoredSportRestore()` with the values its caller rendered
   * with; not meant to be called directly.
   */
  restoreStoredSport: (snapshot: StoredSportSnapshot) => void
  /** Trigger a fetch for a sport's pack. Returns the pack (cached or freshly fetched). */
  loadSport: (id: string) => Promise<SportPack>
  /**
   * #1207: sports whose pack fetch failed with no pack to fall back on. Their
   * callers get the basketball stub for good, so waiting for the pack is over.
   */
  unavailablePacks: Record<string, true>
}

const SportContext = createContext<SportContextType | undefined>(undefined)

// The fan's stored sport (P0, site templates §6.0): the restore/persist
// logic lives in ./storedSport and is applied by this provider; see there.

const SPORT_CACHE_PREFIX = 'mcl_sport_'

function getSportCacheKey(id: string): string {
  return `${SPORT_CACHE_PREFIX}${id}`
}

function readCachedPack(id: string): SportPack | null {
  try {
    const cached = localStorage.getItem(getSportCacheKey(id))
    if (cached) return JSON.parse(cached) as SportPack
  } catch {
    // ignore
  }
  return null
}

function cachePack(pack: SportPack) {
  try {
    localStorage.setItem(getSportCacheKey(pack.id), JSON.stringify(pack))
  } catch {
    // ignore
  }
}

export function SportProvider({ children }: { children: ReactNode }) {
  const { league, reloadForSport } = useLeague()
  const [packs, setPacks] = useState<Record<string, SportPack>>(() => {
    // Seed from localStorage so first paint has data.
    const initial: Record<string, SportPack> = { basketball: FALLBACK_BASKETBALL }
    const ids = ['basketball', 'soccer']  // known sports — pull cached if present
    for (const id of ids) {
      const cached = readCachedPack(id)
      if (cached) initial[id] = cached
    }
    return initial
  })
  // Seed from the cached tenant when we have one (return visit, hot path).
  // First visit on a fresh browser has no cached tenant → activeSportId is
  // null until /public/tenant resolves and the effect below promotes the
  // league's default_sport_id. We let API calls skip the sport stamp until
  // then (better than blasting ?sport=basketball at a soccer-only tenant).
  const [activeSportId, setActiveSportId] = useState<string>(
    () => league?.default_sport_id ?? ''
  )

  // When league data finishes loading, adopt its default_sport_id if the
  // user hasn't picked yet AND it disagrees with our seed (which on a fresh
  // visit was empty). Subsequent SportSwitcher clicks set this explicitly
  // and we don't override.
  const userPickedRef = useRef(false)
  useEffect(() => {
    if (!league?.default_sport_id) return
    if (userPickedRef.current) return
    if (activeSportId === league.default_sport_id) return
    setActiveSportId(league.default_sport_id)
  }, [league?.default_sport_id, activeSportId])

  // Push the active sport into the api.ts module slot so every public API
  // request gets `?sport=X` appended. This is what makes the public site
  // truly per-sport (basketball / soccer behave like two separate sites).
  //
  // DELIBERATELY SET DURING RENDER (not in a useEffect): React commits child
  // effects before parent effects, so a Teams.tsx useEffect on activeSportId
  // fires BEFORE this provider's effect would. If we deferred the slot write
  // to an effect, child data-fetches would race ahead with the stale sport.
  // Setting during render guarantees the slot is current the moment any
  // child sees the new activeSportId.
  //
  // #1224: until the adoption effect above runs, the active sport is the
  // league's default as soon as the league is known. App renders the routes
  // in the same commit the league arrives, so a page's first effects used to
  // see '' and fetch unstamped, then fetch everything again once the effect
  // adopted the default. A league always has a default (NOT NULL); basketball
  // is the same fallback useSportForSeason uses, so nothing waits on ''.
  const shownSportId = activeSportId || (league ? league.default_sport_id || 'basketball' : '')
  setPublicSportContext(shownSportId || null)

  // Multi-sport (#35): when the fan flips sports, ask LeagueContext to
  // refetch /public/tenant?sport=X so theme/copy/branding/feature overrides
  // for that sport apply. Single-sport tenants never enter this branch
  // (sports.size < 2), so they keep using the unscoped tenant payload.
  //
  // The ref-based guard avoids a spinloop: setLeague() inside reloadForSport
  // creates a fresh `seasons` array reference, which re-fires this effect
  // while the next response is still in flight. The early `===` check only
  // settles AFTER setLeague propagates, so several re-fires can dispatch
  // duplicate fetches in the meantime. Tracking the in-flight sport in a ref
  // means a second fetch is only kicked off when the user picks a different
  // sport, not on every interim re-render.
  // When the fan flips the top-nav sport switcher, refetch /public/tenant
  // with the new sport so theme/copy/branding overrides apply. We track the
  // last sport we kicked a fetch for in a ref AND keep the dep array tight
  // (only `activeSportId`) to avoid the spinloop the previous shape had:
  //   - listing `league?.seasons` in deps triggered re-runs on every fetch
  //     (the backend returns a fresh array reference each call)
  //   - listing `league?.applied_sport_id` looked safe but raced with the
  //     in-flight fetch (the check passed BEFORE setLeague propagated)
  // Now the only thing that triggers a refetch is the user actually picking
  // a different sport — measured against the ref, not against React state.
  const lastFetchedSportRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeSportId) return
    // Single-sport tenants don't need the per-sport tenant payload — the
    // unscoped /public/tenant call from LeagueContext's mount effect already
    // returned everything they need.
    const sportsOnTenant = new Set((league?.seasons ?? []).map((s) => s.sport_id))
    if (sportsOnTenant.size < 2) return
    if (lastFetchedSportRef.current === activeSportId) return
    // Initial seeding from league.default_sport_id is NOT a user pick — the
    // unscoped /public/tenant fetch from LeagueContext's mount already covers
    // the default. Skip the duplicate ?sport=default fetch and only refire
    // when the fan actually flips the switcher.
    if (!userPickedRef.current && activeSportId === league?.default_sport_id) {
      lastFetchedSportRef.current = activeSportId
      return
    }
    lastFetchedSportRef.current = activeSportId
    void reloadForSport(activeSportId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSportId])

  const [unavailablePacks, setUnavailablePacks] = useState<Record<string, true>>({})
  const markUnavailable = useCallback((id: string) => {
    setUnavailablePacks((prev) => (prev[id] ? prev : { ...prev, [id]: true }))
  }, [])

  const loadSport = useCallback(async (id: string): Promise<SportPack> => {
    if (packs[id]) return packs[id]
    const cached = readCachedPack(id)
    if (cached) {
      setPacks((prev) => ({ ...prev, [id]: cached }))
      // Fire a background refresh — sport packs are mostly stable, but a
      // platform release might extend a pack with new stats.
      void refreshFromNetwork(id)
      return cached
    }
    return refreshFromNetwork(id)
  }, [packs])

  const refreshFromNetwork = useCallback(async (id: string): Promise<SportPack> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'
    try {
      const response = await window.fetch(`${API_BASE_URL}/public/sports/${id}`)
      if (!response.ok) {
        if (!packs[id]) markUnavailable(id)
        return packs[id] ?? FALLBACK_BASKETBALL
      }
      const pack = (await response.json()) as SportPack
      cachePack(pack)
      setPacks((prev) => ({ ...prev, [id]: pack }))
      return pack
    } catch {
      if (!packs[id]) markUnavailable(id)
      return packs[id] ?? FALLBACK_BASKETBALL
    }
  }, [packs, markUnavailable])

  // Preload tenant's default sport once the tenant is known.
  //
  // `loadSport` is intentionally NOT a dep here even though we call it: its
  // identity changes whenever `packs` updates, and refreshFromNetwork inside
  // loadSport calls setPacks on every background refresh — that would make
  // this effect re-fire forever. We pin via ref instead so the effect only
  // re-runs when the set of sport ids on the tenant actually changes.
  const loadSportRef = useRef(loadSport)
  loadSportRef.current = loadSport
  const sportIdsKey = (league?.seasons ?? []).map((s) => s.sport_id).sort().join(',')
  useEffect(() => {
    if (league?.default_sport_id) {
      void loadSportRef.current(league.default_sport_id)
    }
    for (const season of league?.seasons ?? []) {
      void loadSportRef.current(season.sport_id)
    }
  }, [league?.default_sport_id, sportIdsKey])

  // Wrap setActiveSport so any explicit pick (SportSwitcher click, Stats page
  // snapping to a season's sport) flips userPickedRef. From then on we no
  // longer auto-promote the league's default_sport_id over their choice.
  //
  // `{ persist: true }` (the SportSwitcher click) also stores the pick for
  // this host, after the switch, as the switcher did.
  const setActiveSport = useCallback((id: string, opts?: { persist?: boolean }) => {
    userPickedRef.current = true
    setActiveSportId(id)
    if (opts?.persist) writeStoredSport(id)
  }, [])

  // Restore the fan's stored sport (see the P0 block above). Goes through
  // setActiveSport, so a restore (or the default fallback) counts as a pick,
  // exactly as when the switcher called it.
  const restoreStoredSport = useCallback((snapshot: StoredSportSnapshot) => {
    const next = resolveStoredSport(snapshot, readStoredSport())
    if (next) setActiveSport(next)
  }, [setActiveSport])

  // #1118: consumers see each pack's words in the active language (the
  // packs as served in English). The raw packs stay in state and the cache.
  const locale = useLocale()
  const shownPacks = useMemo(
    () => localizePacks(packs),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- locale is read inside localizePacks
    [packs, locale]
  )

  const value = useMemo<SportContextType>(
    () => ({
      packs: shownPacks,
      activeSportId: shownSportId,
      setActiveSport,
      restoreStoredSport,
      loadSport,
      unavailablePacks,
    }),
    [shownPacks, shownSportId, setActiveSport, restoreStoredSport, loadSport, unavailablePacks]
  )

  return <SportContext.Provider value={value}>{children}</SportContext.Provider>
}

// ────────────────────────────────────────────────────────────────────────
// Hooks
// ────────────────────────────────────────────────────────────────────────

/**
 * Get a sport pack.
 *   useSport()          → the active sport (tenant default or page-set).
 *   useSport('soccer')  → the soccer pack specifically.
 *
 * Always returns a non-null pack — falls back to a basketball stub while
 * the network fetch is in flight so callers don't have to handle nulls.
 */
export function useSport(sportId?: string): SportPack {
  const ctx = useContext(SportContext)
  if (ctx === undefined) {
    throw new Error('useSport must be used within a SportProvider')
  }
  const id = sportId || ctx.activeSportId || 'basketball'
  return ctx.packs[id] ?? localizePack(FALLBACK_BASKETBALL)
}

/** Get the pack for a specific season — resolves via TenantInfo.seasons[]. */
export function useSportForSeason(seasonId: number | null | undefined): SportPack {
  const ctx = useContext(SportContext)
  if (ctx === undefined) {
    throw new Error('useSportForSeason must be used within a SportProvider')
  }
  const { league } = useLeague()
  const season = seasonId != null ? league?.seasons?.find((s) => s.id === seasonId) : null
  const id = season?.sport_id || league?.default_sport_id || 'basketball'
  // Trigger load if needed
  useEffect(() => {
    if (id && !ctx.packs[id]) void ctx.loadSport(id)
  }, [id, ctx])
  return ctx.packs[id] ?? localizePack(FALLBACK_BASKETBALL)
}

/**
 * #1207: has the season's pack settled? useSportForSeason returns the
 * basketball stub until a soccer pack arrives, so a page that keys requests
 * on the pack (Stats' leader categories) waits for this before fetching.
 * True once the pack is loaded, or its fetch failed (the stub is then final).
 */
export function useSportPackSettled(seasonId: number | null | undefined): boolean {
  const ctx = useContext(SportContext)
  if (ctx === undefined) {
    throw new Error('useSportPackSettled must be used within a SportProvider')
  }
  const { league } = useLeague()
  const season = seasonId != null ? league?.seasons?.find((s) => s.id === seasonId) : null
  const id = season?.sport_id || league?.default_sport_id || 'basketball'
  return !!ctx.packs[id] || !!ctx.unavailablePacks[id]
}

/** Pull the full sport context — when you need setActiveSport / loadSport. */
export function useSportContext(): SportContextType {
  const ctx = useContext(SportContext)
  if (ctx === undefined) {
    throw new Error('useSportContext must be used within a SportProvider')
  }
  return ctx
}

/** Like useSportContext(), but returns undefined outside a SportProvider
 *  instead of throwing. Used by the label layer so kit components still
 *  render in isolation (tests, share-card capture). */
// eslint-disable-next-line react-refresh/only-export-components -- a hook, like the others in this file
export function useOptionalSportContext(): SportContextType | undefined {
  return useContext(SportContext)
}

/** List the registered sport ids the platform currently supports. */
export function knownSportIds(): string[] {
  return ['basketball', 'soccer']
}

/** Convenience: pretty label for a known sport id, in the active language. */
export function sportDisplayName(id: string): string {
  const words = sportWords(id)
  if (words) return words.name
  switch (id) {
    case 'basketball': return 'Basketball'
    case 'soccer':     return 'Soccer'
    default:           return id.charAt(0).toUpperCase() + id.slice(1)
  }
}
